import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ConfirmationPopup from 'components/ConfirmationPopup/ConfirmationPopup';

import { useParams } from 'react-router-dom';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import { useHistory } from 'react-router-dom';
import { useMaintenanceRequestActions } from 'hooks/MaintenanceRequest';

import {
  EXIT_WITHOUT_SAVING,
  WITHOUT_SAVING_TYPE,
  WITHOUT_SAVING_BY_NAV_TYPE,
  CREATE_MAINTENANCE_REQUEST,
  CREATE_MAINTENANCE_REQUEST_TYPE,
  UPDATE_MAINTENANCE_REQUEST,
  UPDATE_MAINTENANCE_REQUEST_TYPE,
  RESOLVE_MAINTENANCE_REQUEST,
  RESOLVE_MAINTENANCE_REQUEST_TYPE,
  REOPEN_MAINTENANCE_REQUEST,
  REOPEN_MAINTENANCE_REQUEST_TYPE
} from 'constants/dialogPopupsData';
import {
  MAINTENANCE_REQUEST_DETAILS_PATH,
  MAINTENANCE_REQUEST_SUMMARY_PATH
} from 'constants/routeConstants';

import { getErrorsProperties } from 'helpers/ErrorValidator';
import { getBackendErrors } from 'helpers/AppHelpers';

import { Wrapper, BreadCrumbs, Header, Form, Footer } from './components';
import { FORM_FIELDS, getFieldsToValidate, REQUEST_STATUSES } from './helpers';

export function RequestEdit() {
  const { id } = useParams();
  const isMobile = useMobileViewport();
  const history = useHistory();

  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [modalData, setModalData] = useState({});
  const [creationForm, setCreationForm] = useState({});

  const methods = useForm({
    defaultValues: {
      [FORM_FIELDS.JOB_LOCATION]: '',
      [FORM_FIELDS.JOB_NUMBER]: '',
      [FORM_FIELDS.OPENED_ON]: '',
      [FORM_FIELDS.PRIORITY]: {},
      [FORM_FIELDS.REQUESTER_EMAIL]: '',
      [FORM_FIELDS.REQUESTER_NAME]: '',
      [FORM_FIELDS.REQUESTER_PHONE]: '',
      [FORM_FIELDS.SEVERITY]: {},
      [FORM_FIELDS.TASK_LOCATION]: '',
      [FORM_FIELDS.WORK_ORDER_TASK_REQUEST]: '',
      [FORM_FIELDS.COMMENT]: '',
      [FORM_FIELDS.COMMENTS]: [],
      [FORM_FIELDS.STATUS]: undefined
    }
  });
  const { formState, setError, getValues, reset } = methods;
  const { isDirty } = formState;

  const {
    getRequestAction,
    getCreationFromAction,
    createRequestAction,
    updateRequestAction,
    clearStateAction
  } = useMaintenanceRequestActions();
  const { setUnsavedFormDataAction } = useCommonActions();
  const { unsavedFormData } = useCommonSelector();

  useEffect(() => {
    if (id) {
      getRequestAction(id).then((res) => res?.id && reset(res));
    }
  }, [id]);

  useEffect(() => {
    getCreationFromAction().then((res) => setCreationForm(res || {}));
  }, []);

  useEffect(() => {
    if (id) {
      setBreadcrumbs([
        { path: `${MAINTENANCE_REQUEST_DETAILS_PATH}/${id}`, name: `Request #${id}` },
        { name: `Edit request` }
      ]);
    } else {
      setBreadcrumbs([{ name: `Create request` }]);
    }
  }, [id]);

  useEffect(() => {
    if (isDirty && !unsavedFormData) {
      setUnsavedFormDataAction(true);
    }
    return () => {
      setUnsavedFormDataAction(false);
    };
  }, [isDirty]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const closeModal = () => setModalData({});
  const agreeModal = () => {
    const formValues = getValues();

    const handleResponse = (err) =>
      err?.errors &&
      getBackendErrors(err).forEach(({ name, type, message }) => setError(name, { type, message }));

    const createCommentPayload = (statusName) => {
      const status = statusName
        ? creationForm.statuses.find(({ name }) => name === statusName)
        : undefined;

      const newComment = {
        description: formValues[FORM_FIELDS.COMMENT]
      };

      return {
        ...formValues,
        ...(status && { status }),
        [FORM_FIELDS.COMMENTS]: [...(formValues[FORM_FIELDS.COMMENTS] || []), newComment]
      };
    };

    const modalHandlers = {
      [CREATE_MAINTENANCE_REQUEST_TYPE]: () => createRequestAction(formValues).then(handleResponse),

      [UPDATE_MAINTENANCE_REQUEST_TYPE]: () =>
        updateRequestAction(createCommentPayload()).then(handleResponse),

      [RESOLVE_MAINTENANCE_REQUEST_TYPE]: () =>
        updateRequestAction(createCommentPayload(REQUEST_STATUSES.RESOLVED)).then(handleResponse),

      [REOPEN_MAINTENANCE_REQUEST_TYPE]: () =>
        updateRequestAction(createCommentPayload(REQUEST_STATUSES.OPENED)).then(handleResponse),

      [WITHOUT_SAVING_TYPE]: () => getBack(),

      [WITHOUT_SAVING_BY_NAV_TYPE]: () => history.push(modalData.selectedRouteUrl)
    };

    const action = modalHandlers[modalData.type];
    if (action) action();

    closeModal();
  };

  const getBack = () =>
    history.push(
      id ? `${MAINTENANCE_REQUEST_DETAILS_PATH}/${id}` : MAINTENANCE_REQUEST_SUMMARY_PATH
    );

  const handleCancelClick = () => {
    if (isDirty) {
      setModalData(EXIT_WITHOUT_SAVING);
    } else {
      getBack();
    }
  };

  const validateForm = () => {
    const fieldsToValidate = getFieldsToValidate(getValues());

    let isFormValid = true;
    fieldsToValidate.forEach(({ name, errorMessage, isInvalid, checkCondition = true }) => {
      if (isInvalid && checkCondition) {
        setError(name, getErrorsProperties(errorMessage));
        isFormValid = false;
      }
    });

    return isFormValid;
  };

  const handleEditRequest = (modal) => {
    if (!validateForm()) return;

    setModalData({ ...modal, isOpened: true });
  };

  return (
    <FormProvider {...methods}>
      <Wrapper>
        {!isMobile && <BreadCrumbs crumbs={breadcrumbs} setModalData={setModalData} />}

        <Header onCancel={handleCancelClick} />

        <Form creationForm={creationForm} />

        <Footer
          onCancel={handleCancelClick}
          onEdit={() =>
            handleEditRequest(id ? UPDATE_MAINTENANCE_REQUEST : CREATE_MAINTENANCE_REQUEST)
          }
          onResolve={() => handleEditRequest(RESOLVE_MAINTENANCE_REQUEST)}
          onReopen={() => handleEditRequest(REOPEN_MAINTENANCE_REQUEST)}
        />
      </Wrapper>

      <ConfirmationPopup data={modalData} onAgree={agreeModal} onDismiss={closeModal} />
    </FormProvider>
  );
}
